(() => {
  'use strict';
  if (window.AWToolsPersistence?.version) return;

  const VERSION = '1.0.1';
  const CONTEXT_KEY = 'aw_airsoft_context_v1';
  const CONFIG_KEY = 'aw_airsoft_configurator';
  const MODEL_MAP_KEY = 'aw_saved_config_models_v1';

  const normalize = (value = '') => String(value)
    .trim().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' et ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const readJson = (key, fallback = {}) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; }
    catch (_) { return fallback; }
  };
  const writeJson = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const writeText = (key, value) => { try { if (value) localStorage.setItem(key, value); else localStorage.removeItem(key); } catch (_) {} };

  const toolSource = () => document.querySelector('[data-aw-tools-source]');
  const modelNodes = () => Array.from(document.querySelectorAll('[data-aw-tool-model]'));
  const recordFromNode = (node) => node ? {
    handle: String(node.dataset.handle || '').trim(),
    label: String(node.dataset.label || '').trim(),
    model: String(node.dataset.model || '').trim(),
    manufacturer: String(node.dataset.manufacturer || '').trim(),
    platform: String(node.dataset.platform || '').trim(),
    platformLabel: String(node.dataset.platformLabel || '').trim(),
    type: String(node.dataset.type || '').trim(),
    pageUrl: String(node.dataset.pageUrl || '').trim()
  } : null;

  const findModel = (value = '') => {
    const needle = normalize(value);
    if (!needle) return null;
    const node = modelNodes().find((item) => [item.dataset.handle, item.dataset.label, item.dataset.model]
      .some((candidate) => normalize(candidate) === needle));
    return recordFromNode(node);
  };

  const decodeShare = (value = '') => {
    if (!value) return null;
    try {
      const base64 = String(value).replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const binary = atob(padded);
      const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch (_) { return null; }
  };

  const shareMatchesModel = (share, record) => {
    if (!share || !record) return true;
    if (share.p && normalize(share.p) !== normalize(record.platform)) return false;
    if (share.m && record.manufacturer && normalize(share.m) !== normalize(record.manufacturer)) return false;
    if (share.t && record.type && normalize(share.t) !== normalize(record.type)) return false;
    return true;
  };

  const detailFromRecord = (record) => ({
    model: record.model || record.label,
    modelLabel: record.label || record.model,
    modelHandle: record.handle,
    platform: normalize(record.platform),
    platformLabel: record.platformLabel || record.platform,
    manufacturer: normalize(record.manufacturer),
    manufacturerLabel: record.manufacturer,
    replicaType: normalize(record.type),
    replicaTypeLabel: record.type
  });

  const applyRecord = (record, source = 'exact-model') => {
    if (!record?.platform) return null;
    const detail = detailFromRecord(record);
    if (window.AWAirsoftContext?.write) {
      try { window.AWAirsoftContext.write(detail, source); } catch (_) {}
    } else {
      const previous = readJson(CONTEXT_KEY, {});
      writeJson(CONTEXT_KEY, {
        ...previous, version: 1,
        platform: detail.platform, platformLabel: detail.platformLabel,
        manufacturer: detail.manufacturer, manufacturerLabel: detail.manufacturerLabel,
        type: detail.replicaType, typeLabel: detail.replicaTypeLabel,
        model: detail.model, modelLabel: detail.modelLabel, modelHandle: detail.modelHandle,
        source, updatedAt: new Date().toISOString()
      });
      writeText('aw_selected_platform', detail.platform);
      writeText('aw_selected_manufacturer', detail.manufacturer);
      writeText('aw_selected_replica_type', detail.replicaType);
    }

    const state = readJson(CONFIG_KEY, {});
    const platformChanged = normalize(state.platform) !== detail.platform;
    const previousModel = normalize(state.modelHandle || state.modelLabel || state.model || '');
    const nextModel = normalize(detail.modelHandle || detail.modelLabel || detail.model || '');
    const modelChanged = !!nextModel && previousModel !== nextModel;
    const freshFromCompatibility = source === 'compatibility-url' && new URLSearchParams(location.search).get('aw_new') === '1';
    state.platform = detail.platform;
    state.platformLabel = detail.platformLabel;
    state.manufacturer = detail.manufacturer;
    state.type = detail.replicaType;
    state.model = detail.model;
    state.modelLabel = detail.modelLabel;
    state.modelHandle = detail.modelHandle;
    if (source !== 'saved-configuration') state.savedId = '';
    if (source === 'compatibility-url') {
      state.step = 2;
      state.contextNeedsReview = platformChanged && Array.isArray(state.selected) && state.selected.length > 0;
      if (freshFromCompatibility || modelChanged || !state.name || /^Configuration\s+/i.test(String(state.name))) {
        state.name = `Configuration ${detail.modelLabel || detail.platformLabel}`;
      }
    }
    writeJson(CONFIG_KEY, state);
    writeText('aw_selected_platform', detail.platform);
    writeText('aw_selected_manufacturer', detail.manufacturer);
    writeText('aw_selected_replica_type', detail.replicaType);

    const stored = readJson(CONTEXT_KEY, {});
    if (stored && typeof stored === 'object' && !stored.modelHandle) writeJson(CONTEXT_KEY, { ...stored, modelHandle: detail.modelHandle });
    window.dispatchEvent(new CustomEvent('aw:context-changed', { detail: { ...detail, source } }));
    return detail;
  };

  const removeQueryModel = () => {
    const url = new URL(location.href);
    if (!url.searchParams.has('aw_model')) return;
    url.searchParams.delete('aw_model');
    history.replaceState({}, '', url);
  };

  const restoreFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const requested = params.get('aw_model');
    if (!requested) return null;
    const record = findModel(requested);
    if (!record) return null;
    const share = decodeShare(params.get('aw_config') || '');
    if (share && !shareMatchesModel(share, record)) { removeQueryModel(); return null; }
    const result = applyRecord(record, params.get('aw_from') === 'compatibility' ? 'compatibility-url' : 'shared-exact-model');
    if (params.get('aw_new') === '1') {
      const url = new URL(location.href);
      url.searchParams.delete('aw_new');
      history.replaceState({}, '', url);
    }
    return result;
  };

  const currentRecord = () => {
    const context = window.AWAirsoftContext?.current?.() || readJson(CONTEXT_KEY, {});
    return findModel(context.modelHandle || context.modelLabel || context.model || '');
  };

  const ensureConfiguratorUrlModel = () => {
    if (!/\/pages\/configurateur-airsoft\/?$/.test(location.pathname)) return;
    const record = currentRecord();
    const url = new URL(location.href);
    if (record?.handle) {
      if (url.searchParams.get('aw_model') !== record.handle) {
        url.searchParams.set('aw_model', record.handle);
        history.replaceState({}, '', url);
      }
    } else if (url.searchParams.has('aw_model')) {
      url.searchParams.delete('aw_model');
      history.replaceState({}, '', url);
    }
  };

  const decorateCompatibilityLinks = () => {
    const record = currentRecord();
    if (!record?.handle) return;
    document.querySelectorAll('.aw-tools-configure-cta, [data-aw-tools-context="compatibility"] a[href*="/pages/configurateur-airsoft"]')
      .forEach((link) => {
        try {
          const url = new URL(link.href, location.origin);
          url.searchParams.set('aw_from', 'compatibility');
          url.searchParams.set('aw_model', record.handle);
          url.searchParams.set('aw_new', '1');
          link.href = url.toString();
        } catch (_) {}
      });
  };

  const injectOverflowPlatforms = () => {
    const source = toolSource();
    if (!source) return;
    const overflow = Array.from(source.querySelectorAll('[data-aw-tool-platform]'));
    const manual = document.querySelector('[data-aw-compatibility-search] [data-aw-manual-platform]');
    if (manual) {
      const known = new Set(Array.from(manual.options).map((option) => normalize(option.value)));
      overflow.forEach((node) => {
        const handle = normalize(node.dataset.handle);
        if (!handle || known.has(handle)) return;
        const option = document.createElement('option');
        option.value = handle;
        option.textContent = node.dataset.label || handle;
        option.dataset.awToolsOverflow = 'true';
        manual.appendChild(option);
        known.add(handle);
      });
    }
    const platformSource = document.querySelector('[data-aw-compatibility-search] [data-aw-platform-source]');
    if (platformSource) {
      const known = new Set(Array.from(platformSource.querySelectorAll('[data-aw-platform]')).map((node) => normalize(node.dataset.handle)));
      overflow.forEach((node) => {
        const handle = normalize(node.dataset.handle);
        if (!handle || known.has(handle)) return;
        const span = document.createElement('span');
        span.dataset.awPlatform = '';
        span.dataset.handle = handle;
        span.dataset.label = node.dataset.label || handle;
        span.dataset.family = node.dataset.family || '';
        span.dataset.awToolsOverflow = 'true';
        platformSource.appendChild(span);
        known.add(handle);
      });
    }
  };

  const modelMap = () => {
    const value = readJson(MODEL_MAP_KEY, {});
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  };

  const saveCurrentModelMapping = () => {
    const state = readJson(CONFIG_KEY, {});
    const id = String(state.savedId || '');
    const record = currentRecord();
    if (!id || !record) return false;
    const map = modelMap();
    map[id] = { ...record, savedAt: new Date().toISOString() };
    writeJson(MODEL_MAP_KEY, map);
    annotateSavedList();
    return true;
  };

  const restoreSavedModel = (id = '') => {
    const stored = modelMap()[String(id || '')];
    const canonical = stored ? findModel(stored.handle || stored.label || stored.model) : null;
    if (!canonical) return false;
    const state = readJson(CONFIG_KEY, {});
    if (state.platform && normalize(state.platform) !== normalize(canonical.platform)) return false;
    applyRecord(canonical, 'saved-configuration');
    ensureConfiguratorUrlModel();
    window.AWToolsCoherence?.refresh?.();
    return true;
  };

  const deleteSavedModelMapping = (id = '') => {
    const key = String(id || '');
    if (!key) return;
    const map = modelMap();
    if (!Object.prototype.hasOwnProperty.call(map, key)) return;
    delete map[key];
    writeJson(MODEL_MAP_KEY, map);
  };

  const annotateSavedList = () => {
    const map = modelMap();
    document.querySelectorAll('[data-aw-saved-load]').forEach((button) => {
      const record = map[String(button.dataset.awSavedLoad || '')];
      let label = button.querySelector('[data-aw-tools-saved-model]');
      if (!record) { label?.remove(); return; }
      if (!label) {
        label = document.createElement('small');
        label.dataset.awToolsSavedModel = 'true';
        label.className = 'aw-tools-saved-model';
        button.appendChild(label);
      }
      label.textContent = `Réplique : ${record.label || record.model}`;
    });
  };

  const formProperty = (body, name) => (body instanceof FormData || body instanceof URLSearchParams)
    ? String(body.get(`properties[${name}]`) || '') : '';
  const parseJsonBody = (body) => {
    if (typeof body !== 'string') return null;
    try { const parsed = JSON.parse(body); return parsed && typeof parsed === 'object' ? parsed : null; }
    catch (_) { return null; }
  };
  const directContext = (body) => {
    if (body instanceof FormData || body instanceof URLSearchParams) return {
      platform: formProperty(body, 'Plateforme AW') || formProperty(body, '_AW Platform'),
      manufacturer: formProperty(body, 'Fabricant AW') || formProperty(body, '_AW Manufacturer'),
      type: formProperty(body, 'Type de réplique AW') || formProperty(body, '_AW Replica Type'),
      model: formProperty(body, 'Réplique exacte AW') || formProperty(body, '_AW Model')
    };
    const parsed = parseJsonBody(body);
    if (!parsed) return {};
    const props = Array.isArray(parsed.items) ? (parsed.items[0]?.properties || {}) : (parsed.properties || {});
    return {
      platform: props['Plateforme AW'] || props['_AW Platform'] || '',
      manufacturer: props['Fabricant AW'] || props['_AW Manufacturer'] || '',
      type: props['Type de réplique AW'] || props['_AW Replica Type'] || '',
      model: props['Réplique exacte AW'] || props['_AW Model'] || ''
    };
  };
  const contextMatches = (record, direct) => {
    if (!record || !direct?.platform) return false;
    if (normalize(direct.platform) !== normalize(record.platform) && normalize(direct.platform) !== normalize(record.platformLabel)) return false;
    if (direct.manufacturer && record.manufacturer && normalize(direct.manufacturer) !== normalize(record.manufacturer)) return false;
    if (direct.type && record.type && normalize(direct.type) !== normalize(record.type)) return false;
    if (direct.model && normalize(direct.model) !== normalize(record.label) && normalize(direct.model) !== normalize(record.model)) return false;
    return true;
  };
  const exactProperties = (record) => ({
    'Réplique exacte AW': String(record.label || record.model).slice(0, 180),
    '_AW Model': String(record.label || record.model).slice(0, 180),
    '_AW Model Handle': String(record.handle || '').slice(0, 180)
  });
  const decorateBody = (body, properties) => {
    if (!body || !properties) return body;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      Object.entries(properties).forEach(([key, value]) => { if (!body.get(`properties[${key}]`)) body.set(`properties[${key}]`, value); });
      return body;
    }
    const parsed = parseJsonBody(body);
    if (!parsed) return body;
    if (Array.isArray(parsed.items)) parsed.items = parsed.items.map((item) => ({ ...item, properties: { ...properties, ...(item.properties || {}) } }));
    else parsed.properties = { ...properties, ...(parsed.properties || {}) };
    return JSON.stringify(parsed);
  };

  const installFetchBridge = () => {
    if (!window.fetch || window.fetch.__awToolsPersistence === true) return false;
    const innerFetch = window.fetch.bind(window);
    const wrapped = (input, init = {}) => {
      let nextInit = init;
      try {
        const url = typeof input === 'string' ? input : (input?.url || '');
        const method = String(init?.method || input?.method || 'GET').toUpperCase();
        const cartAdd = method === 'POST' && /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(url);
        if (cartAdd && Object.prototype.hasOwnProperty.call(init, 'body')) {
          const record = currentRecord();
          const direct = directContext(init.body);
          if (record && contextMatches(record, direct)) nextInit = { ...init, body: decorateBody(init.body, exactProperties(record)) };
        }
      } catch (_) {}
      return innerFetch(input, nextInit);
    };
    wrapped.__awToolsPersistence = true;
    wrapped.__awToolsPersistenceInner = innerFetch;
    window.fetch = wrapped;
    return true;
  };

  const refresh = () => {
    injectOverflowPlatforms();
    ensureConfiguratorUrlModel();
    decorateCompatibilityLinks();
    annotateSavedList();
  };

  restoreFromUrl();
  injectOverflowPlatforms();
  installFetchBridge();
  refresh();

  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-aw-config-share]')) ensureConfiguratorUrlModel();
    if (event.target.closest?.('[data-aw-save-confirm]')) window.setTimeout(saveCurrentModelMapping, 0);
    const load = event.target.closest?.('[data-aw-saved-load]');
    if (load) window.setTimeout(() => restoreSavedModel(load.dataset.awSavedLoad || ''), 0);
    const remove = event.target.closest?.('[data-aw-saved-delete]');
    if (remove) window.setTimeout(() => deleteSavedModelMapping(remove.dataset.awSavedDelete || ''), 0);
  }, true);

  window.addEventListener('aw:context-changed', () => queueMicrotask(refresh));
  window.addEventListener('aw:compatibility-changed', () => queueMicrotask(refresh));
  document.addEventListener('shopify:section:load', () => queueMicrotask(refresh));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });

  const savedList = document.querySelector('[data-aw-saved-list]');
  if (savedList && 'MutationObserver' in window) new MutationObserver(() => annotateSavedList()).observe(savedList, { childList: true, subtree: true });

  window.AWToolsPersistence = {
    version: VERSION, restoreFromUrl, currentRecord, saveCurrentModelMapping, restoreSavedModel,
    decorateBody, contextMatches, refresh,
    policy: 'exact-model-only-when-direct-context-matches'
  };
})();